import { useAtomValue } from 'jotai';
import { useMemo } from 'react';

import { useChatTransportMode } from '@/hooks/chat/useChatTransportMode';
import type { ChatTransport } from '@/services/chat/chatTransport';
import { createPrimumChatTransport } from '@/services/chat/createPrimumChatTransport';
import { reportShadowComparison } from '@/services/chat/reportShadowComparison';
import { ShadowChatTransport } from '@/services/chat/shadowTransport';
import { userIdAtom } from '@/services/globalState';
import { selectChatTransport, useChatTransportStore } from '@/services/stores/chatTransportStore';

type ShadowEntry = { viewerId: string; shadowed: ShadowChatTransport };

/**
 * One shadowed wrapper per session transport and viewer, so every reader gets
 * the same object and each conversation is compared once per session rather
 * than once per screen that opens it.
 */
const shadows = new WeakMap<ChatTransport, ShadowEntry>();

function shadowed(transport: ChatTransport, viewerId: string): ShadowChatTransport {
  const existing = shadows.get(transport);

  if (existing?.viewerId === viewerId) {
    return existing.shadowed;
  }

  // A viewer change replaces the shadow; the old one would otherwise keep
  // whatever it opened for the previous viewer.
  existing?.shadowed.shutdownShadow().catch(() => undefined);

  const shadow = new ShadowChatTransport(
    transport,
    createPrimumChatTransport(viewerId),
    reportShadowComparison
  );

  shadows.set(transport, { viewerId, shadowed: shadow });

  return shadow;
}

/**
 * The session's chat transport, or undefined until one is ready.
 *
 * In `shadow` mode the session's transport still answers every call, and the
 * Primum transport is run beside it for comparison; see
 * {@link ShadowChatTransport}.
 */
export function useChatTransport(): ChatTransport | undefined {
  const transport = useChatTransportStore(selectChatTransport);

  const mode = useChatTransportMode();

  const viewerId = useAtomValue(userIdAtom);

  return useMemo(() => {
    if (mode !== 'shadow' || !transport || !viewerId) {
      return transport;
    }

    return shadowed(transport, viewerId);
  }, [mode, transport, viewerId]);
}
